'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

import { useBoolean } from 'codekit';
import { AnimatePresence, motion } from 'framer-motion';

import Link from 'components/Link';
import Logo from 'components/Logo';
import { HEADER_NAVIGATION_LINKS } from 'constants/institutional';
import { cn } from 'utils/cn';
import HeaderButtons from './HeaderButtons';
import MobileMenu from './MobileMenu';
import MobileMenuTrigger from './MobileMenuTrigger';
import ScrollObserver from './ScrollObserver';

import styles from './styles.module.css';

interface HeaderPureProps {
	isAuthenticated: boolean;
}

function HeaderPure(props: HeaderPureProps) {
	// Boolean hooks
	const isActiveMobileMenu = useBoolean();
	const pathname = usePathname();

	// Effects
	useEffect(() => {
		document.body.style.overflow = isActiveMobileMenu.value
			? 'hidden'
			: 'auto';
	}, [isActiveMobileMenu.value]);

	return (
		<header
			id="header"
			className={styles.container}
			data-mobile-menu-active={isActiveMobileMenu.value}
		>
			<ScrollObserver targetSelector="header#header" />

			<div className={cn('content', styles.content)}>
				<Link
					to="/"
					className={styles.logo}
					aria-label="Ir para a página inicial"
					onClick={isActiveMobileMenu.setFalse}
				>
					<div className={styles.for_desktop}>
						<Logo width={110} variant="full" />
					</div>

					<div className={styles.for_mobile}>
						<Logo width={100} variant="full" />
					</div>
				</Link>

				<nav>
					<ul>
						{HEADER_NAVIGATION_LINKS.map((link) => (
							<li
								key={link.label}
								data-active={link.to === pathname}
							>
								<Link<string> to={link.to}>{link.label}</Link>

								{link.to === pathname && (
									<motion.div
										className={styles.active_indicator}
										layoutId="active_indicator"
									/>
								)}
							</li>
						))}
					</ul>
				</nav>

				<div className={styles.buttons}>
					<HeaderButtons isAuthenticated={props.isAuthenticated} />
				</div>

				<MobileMenuTrigger
					isActive={isActiveMobileMenu.value}
					onActiveChange={isActiveMobileMenu.setValue}
				/>
			</div>

			<AnimatePresence>
				{isActiveMobileMenu.value && (
					<MobileMenu onClose={isActiveMobileMenu.setFalse} />
				)}
			</AnimatePresence>
		</header>
	);
}

export default HeaderPure;
